import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { fetchTalentNotificationsInfinite } from '../../requests/TalentUserRequests';
import { useAPI } from '../../config/API';
import { useAuth } from '../../providers/AuthProvider';
import User from '../../models/User';
import {TalentMessageDetailView} from "./TalentMessageDetail";
import useMediaQuery from '../../hooks/useMediaQuery';

const DetailEmpty = () => {
    return (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-2.5 p-10">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-1">
                <svg width="24" height="24" fill="none" stroke="#9BA5B7" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                </svg>
            </div>
            <p className="text-sm font-medium text-slate-500">Select a message to read</p>
            <small className="text-[12.5px]">Your messages appear on the left</small>
        </div>
    );
}

const BADGE_STYLES = {
    broadcast: 'bg-slate-100 text-slate-600',
    discipline: 'bg-violet-100 text-violet-800',
    geo: 'bg-teal-100 text-teal-800',
    interest: 'bg-amber-100 text-amber-800',
    shortlisted: 'bg-orange-100 text-orange-900',
    team: 'bg-emerald-100 text-emerald-800',
    specific: 'bg-violet-100 text-violet-800',
};

const BADGE_LABELS = {
    broadcast: 'Announcement',
    discipline: 'Discipline',
    geo: 'Location',
    interest: 'Project Interest',
    shortlisted: 'Shortlisted',
    team: 'Assignment Team',
    specific: 'Specific Talent',
};

const FILTER_TABS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'project', label: 'Projects' },
    { key: 'announcement', label: 'Announcements' },
    // { key: 'archived', label: 'Archived' },
];

const EMPTY_STATES = {
    all: { title: 'No messages yet', sub: 'Messages will appear here' },
    unread: { title: "You're all caught up", sub: 'All messages have been read' },
    project: { title: 'No project messages', sub: 'Project updates will appear here' },
    announcement: { title: 'No announcements', sub: 'Platform announcements will appear here' },
    archived: { title: 'Nothing archived', sub: 'Archived messages will appear here' },
};

const per_page = 50;

// ==================== Badge ====================
function CategoryBadge({ cat }) {
    return (
        <span className={`text-[10.5px] leading-[15px] font-semibold px-[7px] py-[2px] rounded-full whitespace-nowrap ${cat.tagStyles || BADGE_STYLES.broadcast}`}>
            {cat.tag}
        </span>
    );
    // return (
    //     <span className={`text-[10.5px] font-semibold px-[7px] py-[2px] rounded-full whitespace-nowrap ${BADGE_STYLES[cat] || BADGE_STYLES.broadcast}`}>
    //         {BADGE_LABELS[cat] || cat}
    //     </span>
    // );
}

function ProjectBadge({ name }) {
    return (
        <span className="text-[10.5px] font-medium px-[7px] py-[2px] rounded bg-indigo-50 text-indigo-600 border border-indigo-200">
            {name}
        </span>
    );
}

const CATEGORY_MAP = {
    // 2: 'position',
    // 3: 'opp',
    // null: 'announcement',
    1: {
        type: 'announcement',
        tag: 'Announcement',
        tagStyles: 'bg-slate-100 text-slate-600',
    },
    2: {
        type: 'position',
        tag: 'Matching',
        tagStyles: 'bg-violet-100 text-violet-800',
    },
    3: {
        type: 'opp',
        tag: 'Shortlisted',
        tagStyles: 'bg-orange-100 text-orange-900',
    },
}

// ==================== Message Card ====================
function MessageCard({ msg, isActive, isRead, onClick }) {
    const isUnread = !isRead;

    const cat = CATEGORY_MAP[msg.category];

    return (
        <div
            className={TB.mapClass(
                'flex gap-3 px5 pr-5 pl-3 py-3.5 cursor-pointer border-b border-slate-50 transition-colors duration-100',
                {
                    'bg-indigo-50': isActive,
                    'hover:bg-slate-50': !isActive,
                    'border-l-[3px] border-l-amber-400 pl-[17px]': msg.pinned,
                }
            )}
            onClick={onClick}
        >
            <div className="pt-1.5 flexflex-col gap-2 items-center w3.5">
                {isUnread
                    ? (
                        <Tooltip placement="right" title={msg.is_urgent ? 'Urgent' : 'Unread'}>
                            <div className={`w-[7px] h-[7px] ${false ? 'bg-red-700' : 'bg-indigo-500'} rounded-full`} />
                        </Tooltip>
                    )
                    : <div className="w-[7px]" />
                }
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-[3px]">
                    {/* <div className={`text-[13.5px] text-slate-900 leading-snug ${isUnread ? 'font-semibold' : 'font-medium'}`}>
                        {msg.pinned && '📌 '}{msg.title}
                    </div> */}
                    <div className={`text-[13.5px] text-slate-900 leading-snug ${isUnread ? 'font-semibold' : 'font-medium'} flex items-center gap-2`}>
                        {msg.is_urgent && (
                            <Tooltip placemen="right" title="Urgent">
                                <div>
                                    <i className="fas fa-flag text-red-600" />
                                </div>
                            </Tooltip>
                        )}
                        {msg.title}
                    </div>
                    <div className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">{TB.moment(msg.created_at).fromNow()}</div>
                </div>
                <div className="text-[12.5px] text-slate-500 leading-normal mb-[7px] whitespace-nowrap overflow-hidden text-ellipsis">
                    {TB.truncate((msg.body || '').replace(/<[^>]*>/g, ''), { length: 100 })}
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                    <CategoryBadge cat={cat} />
                    {msg.proj && <ProjectBadge name={msg.proj.name} />}
                    <span className="text-[11.5px] text-slate-400">{msg.sender_name}</span>
                </div>
            </div>
        </div>
    );
}

// ==================== Feed Empty ====================
function FeedEmpty({ filter }) {
    const state = EMPTY_STATES[filter] || EMPTY_STATES.all;
    return (
        <div className="flex-1 flex flex-col items-center justify-center p-10 text-slate-400 gap-2.5">
            <div className="w-16 h-16 bg-slate-50 border border-slate-200 rounded-full flex items-center justify-center mb-2">
                <svg width="22" height="22" fill="none" stroke="#9BA5B7" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                </svg>
            </div>
            <div className="text-[15px] font-semibold text-slate-500">{state.title}</div>
            <div className="text-[13px] text-center max-w-[220px] leading-normal">{state.sub}</div>
        </div>
    );
}

// ==================== Main Component ====================
const TalentMessages = () => {
    const { auth, update: authUpdate } = useAuth();
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const isMobile = useMediaQuery('(max-width: 767px)');
    const urlMessageId = params['*'] ? parseInt(params['*']) : null;
    const [activeFilter, setActiveFilter] = useState('all');
    const [activeMessageId, setActiveMessageId] = useState(null);
    const [readSet, setReadSet] = useState(new Set());
    const [archivedSet, setArchivedSet] = useState(new Set());
    const [deletedSet, setDeletedSet] = useState(new Set());
    const [unreadSet, setUnreadSet] = useState(new Set());
    const [sortBy, setSortBy] = useState('newest');

    const {
        data: messageData,
        isFetching,
        isInitialLoading: isLoading,
        isRefetching,
        error,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery(
        ['fetchTalentNotifications', { per_page }],
        fetchTalentNotificationsInfinite,
        {
            getNextPageParam: (lastPage) => {
                if (lastPage?.current_page && lastPage.current_page < lastPage.last_page) {
                    return lastPage.current_page + 1;
                }
                return undefined;
            },
        }
    );

    // console.log(messageData, 'messageData')

    const {
        data: notificationDetail,
        isInitialLoading: detailLoading,
    } = useAPI(['fetchTalentNotification', activeMessageId], {
        enabled: !!activeMessageId,
        refetchOnMount: false,
        staleTime: 200000,
    });

    const totalMessages = messageData?.pages?.[0]?.total || 0;

    const messages = useMemo(() => {
        const data = messageData?.pages?.flatMap(page => page?.notifications || []) || [];
        return [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }, [messageData]);

    const isMessageRead = useCallback((m) => {
        if (unreadSet.has(m.id)) return false;
        return !!m.read_at || readSet.has(m.id);
    }, [readSet, unreadSet]);

    const filteredMessages = useMemo(() => {
        const filtered = messages.filter(m => {
            if (deletedSet.has(m.id)) return false;
            if (archivedSet.has(m.id)) return activeFilter === 'archived';
            if (activeFilter === 'archived') return false;
            if (activeFilter === 'unread') return !isMessageRead(m) || m.id === activeMessageId;
            if (activeFilter === 'project') return [2, 3].includes(m.category);
            if (activeFilter === 'announcement') return m.category === 1;
            return true;
        });

        const pinned = filtered.filter(m => m.pinned);
        const unpinned = filtered.filter(m => !m.pinned);

        if (sortBy === 'oldest') unpinned.reverse();
        if (sortBy === 'unread') {
            unpinned.sort((a, b) => {
                const aUnread = !isMessageRead(a) ? 1 : 0;
                const bUnread = !isMessageRead(b) ? 1 : 0;
                return bUnread - aUnread;
            });
        }

        return [...pinned, ...unpinned];
    }, [messages, activeFilter, isMessageRead, archivedSet, deletedSet, sortBy, activeMessageId]);


    const openMessage = useCallback((id, { skipNavigate } = {}) => {
        setActiveMessageId(id);
        setReadSet(prev => new Set([...prev, id]));
        setUnreadSet(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

        const msg = messages.find(m => m.id === id);
        if (msg && !isMessageRead(msg)) {
            authUpdate(User.make({ ...auth, unread_notifications_count: Math.max(0, auth.unread_notifications_count - 1) }));
        }

        if (isMobile && !skipNavigate) {
            navigate(`/talent/notifications/${id}`);
        }
    }, [messages, auth, authUpdate, isMessageRead, isMobile, navigate]);

    // console.log(auth, 'AUTH')

    const hasHandledDeepLink = useRef(false);
    useEffect(() => {
        const selectedId = location.state?.selectedMessageId || urlMessageId;
        if (selectedId && messages.length > 0 && !hasHandledDeepLink.current) {
            hasHandledDeepLink.current = true;
            openMessage(selectedId, { skipNavigate: true });
        }
    }, [messages.length, location.state?.selectedMessageId, urlMessageId, openMessage]);

    const markUnread = useCallback((id) => {
        setReadSet(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        setUnreadSet(prev => new Set([...prev, id]));
        authUpdate(User.make({ ...auth, unread_notifications_count: auth.unread_notifications_count + 1 }));
        const request = TB.request(
            TB.api_endpoint('/api/user-notifications/bulk-unread'),
            { notification_ids: [id] }
        );
        request.post().catch(console.warn);
    }, [auth, authUpdate]);

    const markRead = useCallback((id) => {
        setReadSet(prev => new Set([...prev, id]));
        setUnreadSet(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        authUpdate(User.make({ ...auth, unread_notifications_count: Math.max(0, auth.unread_notifications_count - 1) }));
        const request = TB.request(
            TB.api_endpoint('/api/user-notifications/bulk-read'),
            { notification_ids: [id] }
        );
        request.post().catch(console.warn);
    }, [auth, authUpdate]);

    const deleteMessage = useCallback((id) => {
        setDeletedSet(prev => new Set([...prev, id]));
        setActiveMessageId(null);
        if (isMobile) navigate('/talent/notifications');

        queryClient.setQueryData(['fetchTalentNotifications', { per_page }], (old) => {
            if (!old) return old;
            return {
                ...old,
                pages: old.pages.map((page, i) => ({
                    ...page,
                    total: Math.max(0, (page.total || 0) - 1),
                })),
            };
        });

        const request = TB.request(
            TB.api_endpoint('/api/user-notifications/bulk-delete'),
            { notification_ids: [id] }
        );
        request.post().catch(console.warn);
    }, [queryClient, isMobile, navigate]);

    const archiveMessage = useCallback((id) => {
        setArchivedSet(prev => new Set([...prev, id]));
        setActiveMessageId(null);
    }, []);

    const listRef = useRef(null);
    const handleScroll = useCallback(() => {
        const el = listRef.current;
        if (!el || !hasNextPage || isFetchingNextPage) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const markAllRead = useCallback(async () => {
        const unreadIds = messages.filter(m => !m.read_at).map(m => m.id);
        if (unreadIds.length === 0) return;

        try {
            const request = TB.request(
                TB.api_endpoint('/api/user-notifications/bulk-read'),
                { notification_ids: null }
            );
            await request.post().catch(console.warn);
            authUpdate(User.make({ ...auth, unread_notifications_count: 0 }));
            setReadSet(prev => new Set([...prev, ...unreadIds]));
            setUnreadSet(new Set());
        } catch (error) {
            await TB.showServerErrorToast();
        }
    }, [messages, auth, authUpdate]);

    const goBack = useCallback(() => {
        setActiveMessageId(null);
        navigate('/talent/notifications');
    }, [navigate]);

    const showMobileDetail = isMobile && activeMessageId;

    // 135px = height of header + navbar
    return (
        <div className="flex h-[calc(100vh-135px)] overflow-hidden bg-slate-50 relative">
            {/* Feed column */}
            <div className={`${isMobile ? 'w-full' : 'w-[420px] min-w-[420px]'} flex flex-col border-r border-slate-200 bg-white h-full overflow-hidden`}>
                {/* Pinned banner */}
                {/* <div className="flex items-center gap-1.5 px-4 py-[7px] bg-amber-50 border-b border-amber-200 text-[12px] font-medium text-amber-800 shrink-0">
                    <svg width="13" height="13" fill="#F59E0B" viewBox="0 0 24 24"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26 12,2" /></svg>
                    Pinned — Profile visibility update requires your attention
                </div> */}

                {/* Header */}
                <div className="px-5 pt-5 shrink-0">
                    <div className="flex items-center justify-between mb-3.5">
                        <div className="flex gap-2">
                            <h2 className="text-lg font-bold tracking-tight">Notifications</h2>
                            {!!totalMessages && (
                                <p className="text-sm text-slate-500">({totalMessages})</p>
                            )}
                        </div>
                        <button
                            // className="flex items-center gap-[5px] px-3 py-1.5 border border-slate-200 rounded-md bg-transparent text-[12.5px] font-medium text-slate-500 cursor-pointer transition-colors hover:bg-slate-50 hover:text-slate-900"
                            className="flex items-center gap-[5px] px-3 py-1.5 border border-primary-lightest rounded-md bg-transparent text-[12.5px] font-medium text-primary cursor-pointer transition-colors hover:bg-primary/10 hover:text-primary-darker hover:border-primary-lightest"
                            onClick={markAllRead}
                            title="Mark all read"
                        >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20,6 9,17 4,12" /></svg>
                            Mark All Read
                        </button>
                    </div>

                    {/* Filter tabs */}
                    <div className="flex border-b border-slate-200 -mx-5 px-5">
                        {FILTER_TABS.map(tab => (
                            <button
                                key={tab.key}
                                className={TB.mapClass(
                                    'text-[13px] font-medium px-3 py-2 cursor-pointer border-b-2 -mb-px whitespace-nowrap transition-colors bg-transparent border-none',
                                    {
                                        'text-indigo-600 border-b-indigo-600 font-semibold': activeFilter === tab.key,
                                        'text-slate-500 border-b-transparent hover:text-slate-900': activeFilter !== tab.key,
                                        'pl-0': tab.key === 'all',
                                    }
                                )}
                                onClick={() => setActiveFilter(tab.key)}
                            >
                                {tab.label}
                                {tab.key === 'unread' && (
                                    <span className="text-[11px] text-indigo-500 ml-1">({auth.unread_notifications_count})</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Sort row */}
                <div className="flex items-center gap-1.5 px-5 py-2 text-[12px] text-slate-400 border-b border-slate-50 shrink-0">
                    Sort:
                    <select
                        className="border-none bg-transparent text-[12px] text-slate-500 cursor-pointer outline-none"
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                    >
                        <option value="newest">Newest first</option>
                        <option value="oldest">Oldest first</option>
                        <option value="unread">Unread first</option>
                    </select>
                </div>

                {/* Message list */}
                <div className="flex-1 overflow-y-auto" ref={listRef} onScroll={handleScroll}>
                    {isLoading ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-10 text-slate-400 gap-2.5">
                            <div className="text-[13px]">Loading messages...</div>
                        </div>
                    ) : filteredMessages.length > 0 ? (
                        <>
                            {filteredMessages.map(msg => (
                                <MessageCard
                                    key={msg.id}
                                    msg={msg}
                                    isActive={msg.id === activeMessageId}
                                    isRead={isMessageRead(msg)}
                                    onClick={() => openMessage(msg.id)}
                                />
                            ))}
                            {isFetchingNextPage && (
                                <div className="flex items-center justify-center py-4 text-slate-400 text-[13px]">
                                    Loading more...
                                </div>
                            )}
                        </>
                    ) : (
                        <FeedEmpty filter={activeFilter} />
                    )}
                </div>
            </div>

            {/* Detail panel - slides in from right on mobile */}
            <div className={`${isMobile ? `absolute inset-0 z-10 transition-transform duration-300 ease-in-out ${showMobileDetail ? 'translate-x-0' : 'translate-x-full'}` : 'flex-1'} h-full overflow-hidden flex flex-col bg-slate-50`}>
                {isMobile && (
                    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-white shrink-0">
                        <button
                            className="flex items-center gap-1.5 px-2 py-1.5 bg-transparent border-none text-[13px] font-medium text-primary cursor-pointer"
                            onClick={goBack}
                        >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="15,18 9,12 15,6" /></svg>
                            Back
                        </button>
                    </div>
                )}
                {(notificationDetail || detailLoading) ? (
                    <TalentMessageDetailView
                        msg={notificationDetail}
                        loading={detailLoading}
                        isRead={!detailLoading && isMessageRead(notificationDetail)}
                        onMarkUnread={markUnread}
                        onMarkRead={markRead}
                        onArchive={archiveMessage}
                        onDelete={deleteMessage}
                    />
                ) : (
                    !isMobile && <DetailEmpty />
                )}
            </div>
        </div>
    );
};

export default TalentMessages;